import * as THREE from "three";

// Floating name tag drawn on a canvas (procedural, no font assets beyond the
// system monospace) and shown as a camera-facing Sprite above a player's head.

/** How close (world units, horizontal) the local player must be for a name tag
 *  to show. Nameplates are hidden past this so distant NPCs/remotes don't litter
 *  the screen; the per-frame toggle lives in npc/index.ts + net.ts (the sprite is
 *  a child of the rig, so an invisible rig hides its label for free). */
export const NAMEPLATE_RANGE = 16;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function makeNameLabel(text: string, subtitle?: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  const font = "bold 48px ui-monospace, monospace";
  const subFont = "bold 32px ui-monospace, monospace";
  ctx.font = font;
  let w = Math.ceil(ctx.measureText(text).width) + 56;
  if (subtitle) {
    ctx.font = subFont;
    w = Math.max(w, Math.ceil(ctx.measureText(subtitle).width) + 56);
  }
  const h = subtitle ? 126 : 80; // taller plate when a title line rides under the name
  canvas.width = w;
  canvas.height = h;

  ctx.font = font; // resizing the canvas resets the context
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  roundRect(ctx, 3, 3, w - 6, h - 6, 18);
  ctx.fill();
  // Kept just under the bloom luminance threshold (BLOOM.threshold = 0.82) so the
  // name reads as bright white text but doesn't halo through the bloom pass.
  ctx.fillStyle = "#cccccc";
  ctx.fillText(text, w / 2, subtitle ? 43 : h / 2 + 3);
  if (subtitle) {
    // Earned title (Plan 09): a smaller gold line under the name — also kept
    // under the bloom threshold so it glints without haloing.
    ctx.font = subFont;
    ctx.fillStyle = "#ffd86a";
    ctx.fillText(subtitle, w / 2, 96);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  // depthTest off so a name stays readable even when the player is behind a hill.
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.renderOrder = 10;
  const s = 0.009; // world scale — a touch smaller than the old 0.011
  sprite.scale.set(w * s, h * s, 1);
  return sprite;
}
